import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Loader, FileText, Eye } from 'lucide-react';
import { examsService } from '../services/api';

/**
 * Componente que mostra o status da análise automática de audiogramas por IA
 * Exibe confiança, dados extraídos e permite validação manual
 */
const AudiogramAnalysisStatus = ({ examId }) => {
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);
  const [pollingAttempts, setPollingAttempts] = useState(0);

  useEffect(() => {
    if (examId) {
      fetchAnalysisStatus();
    }
  }, [examId]);

  // Polling automático: verifica dados a cada 5 segundos por até 2 minutos (24 tentativas)
  useEffect(() => {
    if (!examId || !polling || pollingAttempts >= 24) {
      return;
    }

    const intervalId = setInterval(() => {
      setPollingAttempts(prev => prev + 1);
      fetchAnalysisStatus();
    }, 5000); // Poll a cada 5 segundos

    return () => clearInterval(intervalId);
  }, [examId, polling, pollingAttempts]);

  const fetchAnalysisStatus = async () => {
    try {
      setLoading(true);
      const examData = await examsService.getById(examId);
      
      // Verificar se há dados no audiograma
      const hasData = examData.audiogram?.air && 
        Object.keys(examData.audiogram.air).length > 0;

      if (hasData) {
        // Tentar extrair metadata da análise
        let metadata = null;
        try {
          metadata = JSON.parse(examData.ambienteJson || '{}');
        } catch (e) {
          console.warn('Não foi possível parsear metadata da análise');
        }

        setAnalysisData({
          hasData: true,
          confidence: metadata?.confidence || 'unknown',
          observations: metadata?.observations || '',
          extractedAt: metadata?.extractedAt,
          extractedFrom: metadata?.extractedFrom,
          thresholdCount: Object.keys(examData.audiogram.air).length,
          audiogram: examData.audiogram,
          speechTest: examData.speechTest
        });
        // Parar polling se dados foram encontrados
        setPolling(false);
      } else {
        // Verificar se houve erro na análise automática através dos metadados
        let metadata = null;
        try {
          metadata = JSON.parse(examData.ambienteJson || '{}');
        } catch (e) {
          // Ignorar erro de parse
        }

        // Se metadata indica erro de análise, parar de fazer polling
        if (metadata?.analysisError || metadata?.analysisFailed) {
          setAnalysisData({
            hasData: false,
            error: true,
            message: '❌ A análise automática falhou. Por favor, preencha os dados manualmente.'
          });
          setPolling(false);
          setError('Análise automática do audiograma falhou. Você deve preencher os dados manualmente no próximo passo.');
        } else {
          setAnalysisData({
            hasData: false,
            message: 'Aguardando análise automática pela IA... Isso pode levar até 2 minutos.'
          });
          // Iniciar polling se ainda não há dados e não atingiu limite de tentativas
          if (pollingAttempts < 24) {
            setPolling(true);
          }
        }
      }
    } catch (err) {
      setError(err.message);
      setPolling(false);
    } finally {
      setLoading(false);
    }
  };

  if (loading && pollingAttempts === 0) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
        <Loader className="w-5 h-5 text-blue-600 animate-spin" />
        <div>
          <div className="font-semibold text-blue-900">Verificando análise...</div>
          <div className="text-sm text-blue-600">Buscando dados do audiograma</div>
        </div>
      </div>
    );
  }

  // Mostrar status de polling quando aguardando análise
  if (polling && !analysisData?.hasData) {
    const elapsedSeconds = pollingAttempts * 5;
    const remainingSeconds = Math.max(0, 120 - elapsedSeconds);
    
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
        <Loader className="w-5 h-5 text-yellow-600 animate-spin" />
        <div className="flex-1">
          <div className="font-semibold text-yellow-900">
            🤖 IA analisando o audiograma...
          </div>
          <div className="text-sm text-yellow-700">
            Tentativa {pollingAttempts}/24 - Aguardando extração automática dos dados
          </div>
          <div className="text-xs text-yellow-600 mt-1">
            Tempo restante aproximado: ~{Math.ceil(remainingSeconds / 60)} minuto(s)
          </div>
        </div>
      </div>
    );
  }

  // Timeout: Polling esgotado sem dados
  if (pollingAttempts >= 24 && !analysisData?.hasData) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <div>
          <div className="font-semibold text-red-900">Análise não concluída</div>
          <div className="text-sm text-red-600">
            A IA não conseguiu extrair dados automaticamente após 2 minutos. 
            Verifique se o arquivo enviado é um audiograma válido ou tente novamente.
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <div className="flex-1">
          <div className="font-semibold text-red-900">❌ Erro na análise automática</div>
          <div className="text-sm text-red-600 mt-1">{error}</div>
          <div className="text-xs text-red-500 mt-2">
            💡 <strong>Próximo passo:</strong> Você precisará preencher os dados do audiograma manualmente 
            na tela de "Tipo de Audiometria".
          </div>
        </div>
      </div>
    );
  }

  if (!analysisData?.hasData) {
    // Se tem erro mas hasData=false, mostrar erro visual
    if (analysisData?.error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <div className="flex-1">
            <div className="font-semibold text-red-900">❌ Falha na análise automática</div>
            <div className="text-sm text-red-600 mt-1">{analysisData.message}</div>
            <div className="text-xs text-red-500 mt-2">
              💡 <strong>Importante:</strong> Preencha os dados manualmente no próximo passo 
              ou faça upload de um audiograma mais legível.
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center gap-3">
        <FileText className="w-5 h-5 text-gray-600" />
        <div>
          <div className="font-semibold text-gray-900">Nenhum dado extraído ainda</div>
          <div className="text-sm text-gray-600">{analysisData?.message}</div>
        </div>
      </div>
    );
  }

  const getConfidenceBadge = (confidence) => {
    const badges = {
      high: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        label: 'Alta Confiança',
        icon: CheckCircle
      },
      medium: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        label: 'Confiança Média',
        icon: AlertCircle
      },
      low: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        label: 'Baixa Confiança',
        icon: AlertCircle
      },
      unknown: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        label: 'Confiança Desconhecida',
        icon: Eye
      }
    };

    const badge = badges[confidence] || badges.unknown;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-4 h-4" />
        {badge.label}
      </span>
    );
  };

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <CheckCircle className="w-6 h-6 text-green-600" />
          <div>
            <div className="font-semibold text-green-900 text-lg">
              ✅ Dados Extraídos Automaticamente por IA
            </div>
            <div className="text-sm text-green-700">
              {analysisData.thresholdCount} frequências identificadas
            </div>
          </div>
        </div>
        {getConfidenceBadge(analysisData.confidence)}
      </div>

      {analysisData.extractedFrom && (
        <div className="text-sm text-green-700">
          <strong>Arquivo analisado:</strong> {analysisData.extractedFrom}
        </div>
      )}

      {analysisData.observations && (
        <div className="bg-white border border-green-200 rounded p-3">
          <div className="text-sm font-semibold text-gray-700 mb-1">
            Observações da IA:
          </div>
          <div className="text-sm text-gray-600 italic">
            "{analysisData.observations}"
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded p-3 border border-green-200">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
            Orelha Direita (OD)
          </div>
          <div className="space-y-1">
            {Object.entries(analysisData.audiogram.air).map(([freq, values]) => (
              <div key={freq} className="text-sm flex justify-between">
                <span className="text-gray-600">{freq} Hz:</span>
                <span className="font-medium text-gray-900">
                  {values.re !== null ? `${values.re} dB` : 'N/A'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded p-3 border border-green-200">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
            Orelha Esquerda (OE)
          </div>
          <div className="space-y-1">
            {Object.entries(analysisData.audiogram.air).map(([freq, values]) => (
              <div key={freq} className="text-sm flex justify-between">
                <span className="text-gray-600">{freq} Hz:</span>
                <span className="font-medium text-gray-900">
                  {values.le !== null ? `${values.le} dB` : 'N/A'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {(analysisData.speechTest?.srtRe || analysisData.speechTest?.srtLe) && (
        <div className="bg-white rounded p-3 border border-green-200">
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
            Testes de Fala (SRT)
          </div>
          <div className="grid grid-cols-2 gap-3">
            {analysisData.speechTest.srtRe && (
              <div className="text-sm">
                <span className="text-gray-600">SRT OD:</span>
                <span className="font-medium text-gray-900 ml-2">
                  {analysisData.speechTest.srtRe} dB
                </span>
              </div>
            )}
            {analysisData.speechTest.srtLe && (
              <div className="text-sm">
                <span className="text-gray-600">SRT OE:</span>
                <span className="font-medium text-gray-900 ml-2">
                  {analysisData.speechTest.srtLe} dB
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {analysisData.confidence === 'low' && (
        <div className="bg-yellow-50 border border-yellow-300 rounded p-3 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div className="text-sm text-yellow-800">
            <strong>Atenção:</strong> A confiança da extração foi baixa. 
            Recomendamos revisar manualmente os dados antes de gerar o laudo.
          </div>
        </div>
      )}

      {analysisData.extractedAt && (
        <div className="text-xs text-gray-500 text-center">
          Análise realizada em {new Date(analysisData.extractedAt).toLocaleString('pt-BR')}
        </div>
      )}
    </div>
  );
};

export default AudiogramAnalysisStatus;
